// History tracking: the /api/field-history calls (apps/server/src/fieldHistory.mjs).
// Free of React, so it is unit tested (fieldHistoryApi.test.ts).
import { formatDateTime, humanize } from '../objects/format';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface TrackedField {
  name: string;
  db_type: string;
  tracked: boolean;
  changed_at: string | null;
  changed_by_name: string | null;
}

async function json<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    let message = fallback;
    try {
      const payload = (await response.json()) as { error?: unknown };
      if (typeof payload.error === 'string') message = payload.error;
    } catch {
      // Not JSON.
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

/** The object's fields that can be tracked, in table order. */
export async function fetchTrackedFields(fetcher: Fetch, object: string, init?: RequestInit): Promise<TrackedField[]> {
  const url = `/api/field-history/${encodeURIComponent(object)}`;
  return (await json<{ fields: TrackedField[] }>(await fetcher(url, init), 'Could not load history tracking.')).fields;
}

/** Turns one field's tracking on or off; answers the field as saved. */
export async function setTracked(fetcher: Fetch, object: string, field: string, tracked: boolean): Promise<TrackedField> {
  const url = `/api/field-history/${encodeURIComponent(object)}/${encodeURIComponent(field)}`;
  const init = { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ tracked }) };
  return json<TrackedField>(await fetcher(url, init), `Could not change tracking for ${humanize(field)}.`);
}

/** "Tracked since Oct 22, 2026, 3:05 PM by Pat Example", or the column type when never changed. */
export function trackingNote(field: TrackedField): string {
  if (field.changed_at === null) return field.db_type;
  const by = field.changed_by_name ? ` by ${field.changed_by_name}` : '';
  return `${field.tracked ? 'Tracked since' : 'Turned off'} ${formatDateTime(field.changed_at)}${by}`;
}

/** "2 of 31 fields tracked". */
export function trackedSummary(fields: readonly TrackedField[]): string {
  const on = fields.filter((f) => f.tracked).length;
  return `${on} of ${fields.length} ${fields.length === 1 ? 'field' : 'fields'} tracked`;
}
